/**
 * A parte fixa do dia, e o formulário que a conserta.
 *
 * Mora aqui, e não dentro de uma tela, porque duas telas precisam do mesmo
 * conserto: as Configurações, onde a mãe revê o dia inteiro, e a Semana, onde
 * ela **vê o erro**. A regra 7 do CLAUDE.md diz que o conserto tem que caber na
 * tela onde ela vê o erro — e duas cópias de um formulário são duas cópias que
 * divergem, com a mãe descobrindo qual das duas está velha.
 *
 * Os blocos são o esqueleto do dia: o planner encaixa tarefa nos buracos entre
 * eles, e o protegido não recebe nem tarefa nem aviso (regra 3).
 */

import { useState, type FormEvent } from 'react'

import { hhmm, type BlocoDoDia, type CorrecaoDeBloco, type TipoDeBloco } from '../api'
import { Icone } from './Icone'
import '../estilos/conserto.css'

/**
 * As partes fixas do dia, ditas como a mãe diria.
 *
 * O `tipo` não é enfeite: `sono_crianca`, `mamada` e `tempo_com_filhos` são os
 * blocos que a regra 3 protege.
 */
export const TIPOS_DE_BLOCO: { valor: TipoDeBloco; rotulo: string }[] = [
  { valor: 'trabalho', rotulo: 'trabalho' },
  { valor: 'almoco', rotulo: 'almoço' },
  { valor: 'escola_levar', rotulo: 'levar na escola' },
  { valor: 'escola_buscar', rotulo: 'buscar na escola' },
  { valor: 'tempo_com_filhos', rotulo: 'tempo com as crianças' },
  { valor: 'sono_crianca', rotulo: 'sono da criança' },
  { valor: 'mamada', rotulo: 'mamada' },
  { valor: 'outro', rotulo: 'outra coisa' },
]

export const DIAS_DA_SEMANA = [
  { codigo: 'seg', curto: 'seg' },
  { codigo: 'ter', curto: 'ter' },
  { codigo: 'qua', curto: 'qua' },
  { codigo: 'qui', curto: 'qui' },
  { codigo: 'sex', curto: 'sex' },
  { codigo: 'sab', curto: 'sáb' },
  { codigo: 'dom', curto: 'dom' },
]

/** "seg a sex" quando são seguidos, "seg, qua e sex" quando não são. */
export function diasEmPalavras(dias: string[]): string {
  const ordem = DIAS_DA_SEMANA.map((d) => d.codigo)
  const escolhidos = ordem.filter((d) => dias.includes(d))
  if (escolhidos.length === 7) return 'todo dia'
  if (escolhidos.length === 0) return 'nenhum dia'

  const indices = escolhidos.map((d) => ordem.indexOf(d))
  const seguidos = indices.every((n, i) => i === 0 || n === indices[i - 1] + 1)
  const curto = (d: string) => DIAS_DA_SEMANA.find((x) => x.codigo === d)!.curto

  if (seguidos && escolhidos.length > 2)
    return `${curto(escolhidos[0])} a ${curto(escolhidos[escolhidos.length - 1])}`
  return escolhidos.map(curto).join(', ')
}

/**
 * Uma parte fixa do dia. Tocar abre o conserto, como nas pessoas e nos cômodos.
 *
 * Estes blocos são o esqueleto do dia: o planner encaixa tarefa nos buracos
 * entre eles. Nasciam na primeira conversa e nunca mais mudavam — a casa do
 * Carlos ficou com dois trabalhos sobrepostos e "levar na escola" no domingo, e
 * ele via isso todo dia na Semana sem ter o que tocar.
 */
export function Bloco({
  bloco,
  corrigindo,
  aoCorrigir,
  aoFechar,
  aoSalvar,
  aoTirar,
}: {
  bloco: BlocoDoDia
  corrigindo: boolean
  aoCorrigir: () => void
  aoFechar: () => void
  aoSalvar: (campos: CorrecaoDeBloco) => Promise<void>
  aoTirar: () => Promise<void>
}) {
  const tipo = TIPOS_DE_BLOCO.find((t) => t.valor === bloco.tipo)?.rotulo ?? bloco.tipo

  if (corrigindo) {
    return (
      <FormularioDoBloco
        bloco={bloco}
        aoSalvar={aoSalvar}
        aoFechar={aoFechar}
        aoTirar={aoTirar}
      />
    )
  }

  return (
    <button type="button" className="conf-membro" onClick={aoCorrigir}>
      <span className="conf-membro__icone">
        <Icone nome={bloco.protegido ? 'protegido' : 'calendario'} tamanho={18} />
      </span>
      <span className="conf-membro__nome">{bloco.descricao || tipo}</span>
      <span className="conf-membro__papel">
        {diasEmPalavras(bloco.dias)} · {hhmm(bloco.inicio)}–{hhmm(bloco.fim)}
      </span>
    </button>
  )
}

/** O formulário de um bloco — o mesmo para consertar e para acrescentar. */
export function FormularioDoBloco({
  bloco,
  aoSalvar,
  aoFechar,
  aoTirar,
}: {
  bloco?: BlocoDoDia
  aoSalvar: (campos: CorrecaoDeBloco) => Promise<void>
  aoFechar: () => void
  aoTirar?: () => Promise<void>
}) {
  const [tipo, setTipo] = useState<TipoDeBloco>(bloco?.tipo ?? 'outro')
  const [descricao, setDescricao] = useState(bloco?.descricao ?? '')
  const [dias, setDias] = useState<string[]>(bloco?.dias ?? ['seg', 'ter', 'qua', 'qui', 'sex'])
  const [inicio, setInicio] = useState((bloco?.inicio ?? '09:00:00').slice(0, 5))
  const [fim, setFim] = useState((bloco?.fim ?? '10:00:00').slice(0, 5))
  const [perguntando, setPerguntando] = useState(false)
  const [salvando, setSalvando] = useState(false)

  const salvar = async (evento: FormEvent) => {
    evento.preventDefault()
    if (salvando) return

    const campos: CorrecaoDeBloco = {}
    if (tipo !== bloco?.tipo) campos.tipo = tipo
    if ((descricao.trim() || null) !== (bloco?.descricao ?? null))
      campos.descricao = descricao.trim() || null
    if (dias.join(',') !== (bloco?.dias ?? []).join(',')) campos.dias = dias
    if (`${inicio}:00` !== bloco?.inicio) campos.inicio = `${inicio}:00`
    if (`${fim}:00` !== bloco?.fim) campos.fim = `${fim}:00`

    if (Object.keys(campos).length === 0) {
      aoFechar()
      return
    }
    if (!bloco && dias.length === 0) return

    setSalvando(true)
    await aoSalvar(campos)
    setSalvando(false)
  }

  return (
    <form className="conf-pessoa" onSubmit={salvar}>
      <label className="conf-pessoa__campo">
        <span>O que é</span>
        <select value={tipo} onChange={(e) => setTipo(e.target.value as TipoDeBloco)}>
          {TIPOS_DE_BLOCO.map(({ valor, rotulo }) => (
            <option key={valor} value={valor}>
              {rotulo}
            </option>
          ))}
        </select>
      </label>

      <label className="conf-pessoa__campo">
        <span>Como você chama</span>
        <input
          value={descricao}
          onChange={(e) => setDescricao(e.target.value)}
          placeholder="home office"
          maxLength={200}
        />
      </label>

      <div className="conf-pessoa__idade">
        <label className="conf-pessoa__campo">
          <span>Começa</span>
          <input type="time" value={inicio} onChange={(e) => setInicio(e.target.value)} />
        </label>
        <label className="conf-pessoa__campo">
          <span>Termina</span>
          <input type="time" value={fim} onChange={(e) => setFim(e.target.value)} />
        </label>
      </div>

      <fieldset className="agenda__dias">
        <legend>Em que dias</legend>
        {DIAS_DA_SEMANA.map(({ codigo, curto }) => (
          <label key={codigo} className="agenda__dia-escolha">
            <input
              type="checkbox"
              checked={dias.includes(codigo)}
              onChange={(e) =>
                setDias((antes) =>
                  e.target.checked ? [...antes, codigo] : antes.filter((d) => d !== codigo),
                )
              }
            />
            {curto}
          </label>
        ))}
      </fieldset>

      {perguntando ? (
        <>
          <p className="conf-secao__nota">
            Tiro isso do seu dia. O espaço fica livre pro resto a partir da semana que vem.
          </p>
          <div className="conf-pessoa__acoes">
            <button type="button" className="conf-pessoa__tirar" onClick={() => void aoTirar?.()}>
              Pode tirar
            </button>
            <button
              type="button"
              className="conf-pessoa__deixar"
              onClick={() => setPerguntando(false)}
            >
              Deixa aí
            </button>
          </div>
        </>
      ) : (
        <div className="conf-pessoa__acoes">
          <button type="submit" className="botao-primario" disabled={salvando || !dias.length}>
            {salvando ? 'Salvando…' : 'Salvar'}
          </button>
          <button type="button" className="conf-pessoa__deixar" onClick={aoFechar}>
            Deixa como está
          </button>
          {aoTirar && (
            <button
              type="button"
              className="conf-pessoa__tirar-link"
              onClick={() => setPerguntando(true)}
            >
              Isso não acontece
            </button>
          )}
        </div>
      )}
    </form>
  )
}
